import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage, rows } from "@/lib/api";
import { StatusTimeline } from "@/components/StatusTimeline";
import { PaymentPanel } from "@/components/PaymentPanel";
import { Button } from "@/components/ui/button";
import { rupees, shortDate } from "@/lib/format";
import { usePageTitle } from "@/lib/usePageTitle";

// Non-terminal statuses (BOOKING_TRANSITIONS in core/enums.py): keep polling until the stay is
// completed / cancelled. `checked_in` still awaits the staff "completed".
const OPEN_STATUSES = ["reserved", "confirmed", "checked_in"];
const CANCELLABLE = ["reserved", "confirmed"];
const POLL_MS = 8000;

/** Citizen room/hall booking tracking — `/bookings/:id`, derived from `/bookings/mine`. */
export default function BookingDetail() {
  const { id } = useParams();
  const { t, lang } = useApp();
  usePageTitle(t("bookings.detailTitle")); // R3-10: per-page tab title
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | missing | error
  const [loadError, setLoadError] = useState("");
  const [payment, setPayment] = useState(null);
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [res, pay] = await Promise.all([
        api.get("/bookings/mine"),
        api.get("/payments/mine").catch(() => null),
      ]);
      const found = rows(res.data).find((x) => String(x.id) === String(id));
      if (!found) {
        setItem(null);
        setState("missing");
        return;
      }
      setItem(found);
      setPayment(
        pay
          ? rows(pay.data).find(
              (p) => p.source_type === "booking" && p.source_id === found.id,
            ) || null
          : null,
      );
      setState("ready");
    } catch (e) {
      // A blip during background polling must not blank a page the citizen is already reading.
      setLoadError(apiErrorMessage(e, ""));
      setState((s) => (s === "ready" ? s : "error"));
    }
  }, [id]);

  useEffect(() => {
    setState("loading");
    load();
  }, [load]);

  // A UPI payment has no gateway callback; keep asking while it is open.
  useEffect(() => {
    if (
      !payment ||
      !["pending", "awaiting_confirmation"].includes(payment.status)
    )
      return;
    const interval = setInterval(load, POLL_MS);
    return () => clearInterval(interval);
  }, [payment?.status, load]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep asking while the thing is live: staff confirm on their side, the citizen sees it land here.
  useEffect(() => {
    if (!item || !OPEN_STATUSES.includes(item.status)) return;
    const interval = setInterval(load, POLL_MS);
    return () => clearInterval(interval);
  }, [item?.status, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/bookings/${id}/transition`, { status: "cancelled" });
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  const claimPayment = async (utr) => {
    setPaymentBusy(true);
    setPaymentError("");
    try {
      await api.post(`/payments/${payment.id}/claim`, { utr });
      await load();
    } catch (e) {
      setPaymentError(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setPaymentBusy(false);
    }
  };

  const cancelPayment = async () => {
    setPaymentBusy(true);
    setPaymentError("");
    try {
      await api.post(`/payments/${payment.id}/cancel`);
      await load();
    } catch (e) {
      setPaymentError(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setPaymentBusy(false);
    }
  };

  if (state === "loading")
    return (
      <div
        className="mx-auto max-w-2xl space-y-4 px-5 py-12 sm:px-8"
        data-testid="booking-skeleton"
        aria-busy="true"
      >
        <div className="h-28 animate-pulse rounded-2xl bg-muted" />
        <div className="h-40 animate-pulse rounded-2xl bg-muted" />
      </div>
    );
  if (state === "missing")
    return (
      <div
        className="mx-auto max-w-2xl px-5 py-24 text-center"
        data-testid="booking-not-found"
      >
        <p className="text-lg">{t("bookings.notFound")}</p>
        <Button
          className="mt-6 rounded-full"
          onClick={() => navigate("/activity")}
        >
          {t("bookings.backToActivity")}
        </Button>
      </div>
    );
  if (state === "error")
    return (
      <div
        className="mx-auto max-w-2xl px-5 py-24 text-center"
        data-testid="booking-error"
      >
        <p className="text-lg text-destructive">
          {loadError || t("bookings.loadFailed")}
        </p>
        <Button
          className="mt-6 rounded-full"
          onClick={() => {
            setState("loading");
            load();
          }}
          data-testid="booking-retry-btn"
        >
          {t("common.retry")}
        </Button>
      </div>
    );

  // R6-2: only a room is "stayed in"; a hall/lawn is booked From/Until, with no check-in/out.
  const isStay = item.unit_type == null || item.unit_type === "room";

  return (
    <div
      className="mx-auto max-w-2xl px-5 py-12 sm:px-8"
      data-testid="booking-page"
    >
      <div className="rounded-2xl bg-[hsl(var(--primary))] p-6 text-primary-foreground sm:p-8">
        <p className="text-sm opacity-80">{t("bookings.detailTitle")}</p>
        <h1
          className="mt-1 font-heading text-3xl font-bold tracking-tight"
          data-testid="booking-headline"
        >
          {t("bookings.statusLabels")[item.status] || item.status}
        </h1>
      </div>

      <div className="mt-6 space-y-6">
        <div className="rounded-2xl border border-border p-5">
          <p
            className="font-heading text-lg font-semibold"
            data-testid="booking-title"
          >{`${item.unit_name} · ${item.business_name}`}</p>
          <StatusTimeline
            timeline={item.timeline}
            labelsKey="bookings.statusLabels"
            testId="booking-timeline"
            labelOverrides={isStay ? undefined : t("bookings.hallStatusLabels")}
          />
        </div>

        <div className="rounded-2xl border border-border p-5 text-sm">
          <Row
            label={t(isStay ? "bookings.checkIn" : "bookings.fromDate")}
            value={shortDate(item.start_on, lang)}
          />
          <Row
            label={t(isStay ? "bookings.checkOut" : "bookings.toDate")}
            value={`${shortDate(item.end_on, lang)} · ${item.nights === 1 ? t("bookings.night") : t("bookings.nights", { count: item.nights })}`}
          />
          {item.guests != null && (
            <Row label={t("bookings.guests")} value={item.guests} />
          )}
          <Row
            label={t("bookings.guestName")}
            value={item.guest_snapshot?.name}
          />
          <Row
            label={t("bookings.totalPrice")}
            value={rupees({ minor_units: item.total?.minor_units })}
          />
          {item.payment_mode && (
            <Row
              label={t("payment.modeLabel")}
              value={t(`payment.${item.payment_mode}Mode`)}
            />
          )}
        </div>

        {payment && (
          <PaymentPanel
            payment={payment}
            requireUtr={payment.kind === "checkout"}
            onClaim={claimPayment}
            onCancel={cancelPayment}
            busy={paymentBusy}
            error={paymentError}
            testId="booking-payment"
          />
        )}

        {error && (
          <p
            className="text-sm text-destructive"
            data-testid="booking-error-msg"
          >
            {error}
          </p>
        )}

        {CANCELLABLE.includes(item.status) && (
          <Button
            variant="outline"
            className="rounded-full"
            disabled={busy}
            onClick={cancel}
            data-testid="booking-cancel-btn"
          >
            {t("common.cancel")}
          </Button>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border py-2 last:border-0">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-right">{value}</span>
    </div>
  );
}
